// config/routeConfig.ts - Fixed version with proper route matching + role gating

export interface RouteConfig {
  path: string;
  label: string;
  icon?: string;
  component?: string;
  children?: Record<string, RouteConfig>;
  dynamicLabel?: (context: Record<string, any>) => string;
  hideInSidebar?: boolean;
  hiddenForRoles?: string[]; // roles that should NOT see this in the sidebar
}

export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  dashboard: {
    path: '/',
    label: 'Dashboard',
    icon: 'pi-home',
    component: 'Dashboard',
  },

  batches: {
    path: '/leads',
    label: 'Leads',
    icon: 'pi-users',
    component: 'Leads',
    children: {
      list: {
        path: '/leads',
        label: 'All Leads',
        component: 'Leads',
        hideInSidebar: true,
      },
      details: {
        path: '/leads/:leadId/details',
        label: 'Lead Details',
        component: 'LeadDetails',
        hideInSidebar: true,
      },
    },
  },

  agencySetup: {
    path: '/agency/setup',
    label: 'Agency Setup',
    icon: 'pi-building',
    component: 'AgencySetup',
    hideInSidebar: true,
  },

  leadsimportexport: {
    path: '/leads/import',
    label: 'Import Leads',
    icon: 'pi-upload',
    component: 'ImportLeads',
    hiddenForRoles: ['staff'],
  },


    sheetimports: {
    path: '/sheet-imports',
    label: 'Sheets',
    icon: 'pi-file-excel',   // or 'pi-upload', 'pi-table' — see note below
    component: 'SheetImports',
    hiddenForRoles: ['staff'],   // match whatever gating Import Leads uses
  },

  

  members: {
    path: '/staff-members',
    label: 'Staff',
    icon: 'pi-users',
    component: 'Members',
    hiddenForRoles: ['staff'],
    children: {
      list: {
        path: '/staff-members',
        label: 'Staff',
        component: 'Members',
        hideInSidebar: true,
      },
      details: {
        path: '/batches/:batchId/details',
        label: 'Batch Details',
        component: 'BatchDetails',
        hideInSidebar: true,
      },
    },
  },

    profile: {
    path: '/profile',
    label: 'My Profile',
    icon: 'pi-user',
    component: 'Profile',
    hideInSidebar: false,
  },
};

// Helper functions to work with the route configuration

export interface BreadcrumbItem {
  id: string;
  label: string;
  path: string;
}

/**
 * Flatten all routes for easier lookup
 */
const flattenRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): Array<{ id: string; route: RouteConfig; parent?: string }> => {
  const flattened: Array<{ id: string; route: RouteConfig; parent?: string }> = [];

  for (const [id, route] of Object.entries(config)) {
    flattened.push({ id, route, parent: parentId });

    if (route.children) {
      flattened.push(...flattenRoutes(route.children, id));
    }
  }

  return flattened;
};

// Cache the flattened routes
const FLATTENED_ROUTES = flattenRoutes();

/**
 * Find a route configuration by its path
 * IMPROVED: Uses flattened routes and prioritizes longer, more specific paths
 */
export const findRouteByPath = (
  path: string
): { id: string; route: RouteConfig; parent?: string } | null => {
  // Sort by path length (descending) to match more specific paths first
  const sortedRoutes = [...FLATTENED_ROUTES].sort(
    (a, b) => b.route.path.length - a.route.path.length
  );

  for (const { id, route, parent } of sortedRoutes) {
    // Try exact match first
    if (route.path === path) {
      return { id, route, parent };
    }

    // Then try pattern matching for dynamic routes
    const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pathPattern}$`);

    if (regex.test(path)) {
      return { id, route, parent };
    }
  }

  return null;
};

/**
 * Find a route configuration by its ID
 */
export const findRouteById = (
  id: string,
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  parentId?: string
): { route: RouteConfig; parent?: string } | null => {
  if (config[id]) {
    return { route: config[id], parent: parentId };
  }

  for (const [currentId, route] of Object.entries(config)) {
    if (route.children) {
      const result = findRouteById(id, route.children, currentId);
      if (result) return result;
    }
  }

  return null;
};

/**
 * Build breadcrumb trail from root to current route
 */
export const buildBreadcrumbTrail = (
  currentPath: string,
  context: Record<string, any> = {}
): BreadcrumbItem[] => {
  const trail: BreadcrumbItem[] = [];

  // Always start with dashboard
  trail.push({
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
  });

  // Find current route
  const current = findRouteByPath(currentPath);
  if (!current || current.id === 'dashboard') {
    return trail;
  }

  // Build parent chain
  const buildChain = (parentId?: string): string[] => {
    if (!parentId) return [];

    const parent = findRouteById(parentId);
    if (!parent) return [];

    const chain = buildChain(parent.parent);
    chain.push(parentId);
    return chain;
  };

  const parentChain = buildChain(current.parent);

  // Add parents to trail
  for (const parentId of parentChain) {
    const parentResult = findRouteById(parentId);
    if (parentResult) {
      const label = parentResult.route.dynamicLabel
        ? parentResult.route.dynamicLabel(context)
        : parentResult.route.label;

      trail.push({
        id: parentId,
        label,
        path: parentResult.route.path,
      });
    }
  }

  // Add current route
  const label = current.route.dynamicLabel
    ? current.route.dynamicLabel(context)
    : current.route.label;

  trail.push({
    id: current.id,
    label,
    path: current.route.path,
  });

  return trail;
};

/**
 * Get all routes for sidebar (excluding hidden ones and role-gated ones)
 */
export const getSidebarRoutes = (
  config: Record<string, RouteConfig> = ROUTE_CONFIG,
  role?: string
): Array<{
  id: string;
  route: RouteConfig;
  children?: Array<{ id: string; route: RouteConfig }>;
}> => {
  const routes: Array<{
    id: string;
    route: RouteConfig;
    children?: Array<{ id: string; route: RouteConfig }>;
  }> = [];

  const isHiddenForRole = (r: RouteConfig) =>
    !!role && !!r.hiddenForRoles?.includes(role);

  for (const [id, route] of Object.entries(config)) {
    if (route.hideInSidebar) continue;
    if (isHiddenForRole(route)) continue;

    const item: {
      id: string;
      route: RouteConfig;
      children?: Array<{ id: string; route: RouteConfig }>;
    } = { id, route };

    if (route.children) {
      item.children = [];
      for (const [childId, childRoute] of Object.entries(route.children)) {
        if (childRoute.hideInSidebar) continue;
        if (isHiddenForRole(childRoute)) continue;
        item.children.push({ id: childId, route: childRoute });
      }
    }

    routes.push(item);
  }

  return routes;
};

/**
 * Check if a route or its children are active
 */
export const isRouteActive = (
  routeId: string,
  currentPath: string
): boolean => {
  const current = findRouteByPath(currentPath);
  if (!current) return false;

  // Direct match
  if (current.id === routeId) return true;

  // Check if current route is a child of routeId
  const buildParentChain = (parentId?: string): string[] => {
    if (!parentId) return [];

    const parent = findRouteById(parentId);
    if (!parent) return [];

    const chain = buildParentChain(parent.parent);
    chain.push(parentId);
    return chain;
  };

  const parentChain = buildParentChain(current.parent);
  return parentChain.includes(routeId);
};

/**
 * Navigate to a route by ID
 */
export const getPathForRoute = (
  routeId: string,
  params: Record<string, string> = {}
): string => {
  const result = findRouteById(routeId);
  if (!result) return '/';

  let path = result.route.path;

  // Replace dynamic params
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value);
  }

  return path;
};